import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { gray } from "../../../../theme/themePrimitives";
import { useCustomisation } from "../../../../context/CustomisationContext";

interface ExportMenuProps {
  onExportPDF: () => void | Promise<void>;
  onExportExcel: () => void;
  onExportCertificates?: () => void | Promise<void>;
  isCertificatesLoading?: boolean;
  className?: string;
  singleCertificateExport?: boolean;
  isCertificateDownloadEnabled?: boolean;
}

export const ExportMenu: React.FC<ExportMenuProps> = ({
  onExportPDF,
  onExportExcel,
  onExportCertificates,
  isCertificatesLoading = false,
  className,
  singleCertificateExport = true,
  isCertificateDownloadEnabled = true,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [certDownloadStarted, setCertDownloadStarted] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const open = Boolean(anchorEl);

  const { customisations } = useCustomisation();
  const {
    GPC: { navbarSettings },
  } = customisations.customisations;

  const gpcNavbarDropdownBgColor = navbarSettings?.navbarDropdownBgColor || undefined;
  const gpcNavbarDropdownTextColor = navbarSettings?.navbarDropdownTextColor;

  useEffect(() => {
    if (certDownloadStarted && !isCertificatesLoading) {
      setAnchorEl(null);
      setCertDownloadStarted(false);
    }
  }, [isCertificatesLoading, certDownloadStarted]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    if (isCertificatesLoading || pdfLoading) return;
    setAnchorEl(null);
  };

  const handlePDFClick = async () => {
    setPdfLoading(true);
    try {
      await onExportPDF();
    } finally {
      setPdfLoading(false);
      setAnchorEl(null);
    }
  };

  const handleExcelClick = () => {
    handleClose();
    onExportExcel();
  };

  const handleCertificatesClick = async () => {
    setCertDownloadStarted(true);
    await onExportCertificates?.();
  };

  return (
    <Box className={className} data-html2canvas-ignore="true">
      <IconButton
        id="export-menu-button"
        onClick={handleClick}
        sx={{
          padding: 0.5,
          borderRadius: 2,
          borderColor: gpcNavbarDropdownTextColor,
          backgroundColor: gpcNavbarDropdownBgColor,
        }}
        aria-label="export options"
        aria-controls={open ? "export-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        <MoreHorizIcon
          sx={{ color: gpcNavbarDropdownTextColor, fontSize: 28 }}
        />
      </IconButton>
      <Menu
        id="export-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              boxShadow:
                "0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)",
              width: 240,
              mt: 0.5,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #EAECF0" }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              color: "#1F2937",
            }}
          >
            Export report as...
          </Typography>
        </Box>
        <MenuItem
          onClick={handlePDFClick}
          disabled={pdfLoading}
          sx={{
            display: { xs: "none", md: "flex" },
            px: 2,
            py: 1.5,
            mt: 0.5,
            "&:hover": {
              backgroundColor: gray[100],
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: "#101828",
                flex: 1,
              }}
            >
              PDF
            </Typography>
            {pdfLoading && <CircularProgress size={30} />}
          </Box>
        </MenuItem>
        <MenuItem
          onClick={handleExcelClick}
          sx={{
            px: 2,
            py: 1.5,
            "&:hover": {
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              color: "#101828",
            }}
          >
            CSV
          </Typography>
        </MenuItem>
        {onExportCertificates && (
          <MenuItem
            onClick={handleCertificatesClick}
            disabled={isCertificatesLoading || !isCertificateDownloadEnabled}
            sx={{
              px: 2,
              py: 1.5,
              "&:hover": {
                backgroundColor: "#F9FAFB",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#101828",
                  flex: 1,
                }}
              >
                {singleCertificateExport ? "Certificate" : "Certificates"}
              </Typography>
              {isCertificatesLoading && <CircularProgress size={30} />}
            </Box>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
